/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/**
 * Display helpers for env type and envTypeConfig names in the workspace info card tiles.
 *
 * Config names are admin-authored, up to 128 characters, and conventionally start with a workspace
 * code such as "C06", "C107" or "G03". The tiles only have room for roughly 25 characters, so the
 * code is pulled out into its own badge and the remainder is truncated in the middle, which keeps
 * both ends visible. The trailing part carries the distinguishing hardware detail (the codebase
 * branches on names containing "A10G" in SelectConfigurationCards and CreateInternalEnvForm), so a
 * trailing ellipsis would hide exactly the part that tells two configs apart.
 *
 * Env type names carry the OS and software in a single "<OS> - <Software>" string, and retired
 * types get renamed to "_<name>-DEPRECATED" by the catalogue upgrade flow. Both live here so the
 * workspace card and the workspace detail page cannot drift apart, which they previously did by
 * each keeping a private copy of the parsing.
 */

// One to three UPPERCASE letters followed by one to four digits, then a separator, then a non-empty
// remainder. Uppercase is required on purpose: it is the actual convention (C06, C77, C107, G03) and
// it stops lowercase config ids such as "ec2-linux-gpu-g5-xlarge" being misread as code "EC2" when
// the card falls back to rendering an id because the config has not loaded.
const WORKSPACE_CODE_PATTERN = /^([A-Z]{1,3}\d{1,4})[\s\-_:]+(\S.*)$/;

// Kept just under the ~25 characters the tile fits at its narrowest, leaving the CSS ellipsis as a
// backstop rather than the primary mechanism.
const DEFAULT_MAX_LENGTH = 24;

const ELLIPSIS = '\u2026';

/**
 * Splits a leading workspace code off a config name.
 *
 * Returns `{ code: null, rest: <name> }` when there is no recognisable code, or when stripping the
 * code would leave nothing behind, so an unconventionally named config still renders its full name.
 */
function parseConfigName(name) {
  if (typeof name !== 'string') return { code: null, rest: '' };

  const trimmed = name.trim();
  const match = WORKSPACE_CODE_PATTERN.exec(trimmed);
  if (!match) return { code: null, rest: trimmed };

  return { code: match[1], rest: match[2].trim() };
}

/**
 * Shortens text by removing characters from the middle, preserving both ends.
 *
 * The split point snaps back to the start of the trailing word where it can, so a token like
 * "A10G" is kept whole instead of being sliced into "10G". The tail is allowed to claim up to the
 * larger half of the budget to make that fit; beyond that the head would lose too much context, so
 * it falls back to a plain character split.
 *
 * The ellipsis counts towards maxLength, so the result is never longer than maxLength.
 */
function middleTruncate(text, maxLength = DEFAULT_MAX_LENGTH) {
  if (typeof text !== 'string') return '';
  if (maxLength < 1) return '';
  if (text.length <= maxLength) return text;
  if (maxLength === 1) return ELLIPSIS;

  const keep = maxLength - 1;
  const half = Math.floor(keep / 2);
  const maxTail = keep - half;

  let tailStart = text.length - half;
  const earliestTailStart = text.length - maxTail;
  for (let i = tailStart; i >= earliestTailStart; i -= 1) {
    if (/\s/.test(text.charAt(i - 1))) {
      tailStart = i;
      break;
    }
  }

  const tail = text.slice(tailStart).trimStart();
  const head = text.slice(0, keep - tail.length).trimEnd();

  return `${head}${ELLIPSIS}${tail}`;
}

/**
 * Prepares a config name for display in an info card row.
 *
 * @param {string} name the raw envTypeConfig name, or a fallback such as the config id
 * @param {number} maxLength how many characters the remainder may occupy
 * @returns {{ code: string|null, display: string, full: string, truncated: boolean }}
 *   `code` for the badge, `display` for the row value, `full` for the hover popup, and `truncated`
 *   indicating whether `display` lost any characters, so callers can skip a pointless popup.
 */
function formatConfigName(name, maxLength = DEFAULT_MAX_LENGTH) {
  const full = typeof name === 'string' ? name.trim() : '';
  const { code, rest } = parseConfigName(full);
  const display = middleTruncate(rest, maxLength);

  return { code, display, full, truncated: display !== rest };
}

// Retired env types are renamed by the catalogue upgrade flow in parts/catalogue/
// CatalogueProductDetail.js as `_${name}-DEPRECATED`. Keep these in step with the constants there.
const DEPRECATED_MARKER = 'DEPRECATED';
const DEPRECATED_NAME_PREFIX = '_';

/**
 * True when a type or config name carries the DEPRECATED marker used to retire it from new
 * workspace creation. Case-sensitive substring match, matching the convention documented above.
 * Shared by SelectEnvTypeStep (workspace types) and ConfigureEnvTypeStep (workspace configs) so
 * both steps hide retired items the same way.
 */
function isDeprecatedName(name) {
  return typeof name === 'string' && name.includes(DEPRECATED_MARKER);
}

// Strips the leading underscore(s) and the trailing -DEPRECATED suffix. Applied repeatedly so a name
// that somehow went through deprecation twice still comes out clean.
function stripDeprecationMarkers(name) {
  let result = name;
  let previous;

  do {
    previous = result;
    result = result.replace(new RegExp(`^\\${DEPRECATED_NAME_PREFIX}+`), '');
    result = result.replace(new RegExp(`[\\s\\-_]*${DEPRECATED_MARKER}\\s*$`), '');
    result = result.trim();
  } while (result !== previous);

  return result;
}

/**
 * Splits an env type name into its OS and software parts, and reports whether it is deprecated.
 *
 * Names follow "<OS> - <Software>", or are a bare OS name for base workspaces. Deprecation markers
 * are removed before the split, because otherwise they land in a different field depending on the
 * workspace: "_Ubuntu - Chemistry-DEPRECATED" put "-DEPRECATED" on the software, while a base
 * workspace with no " - " separator put "_...-DEPRECATED" on the OS. Stripping first means the OS
 * and software rows read the same either way, and the deprecation is surfaced on its own instead.
 *
 * @returns {{ os: string, software: string, deprecated: boolean }}
 */
function parseEnvTypeName(typeName) {
  if (typeof typeName !== 'string' || typeName.trim() === '') {
    return { os: 'Unknown', software: 'Base', deprecated: false };
  }

  const raw = typeName.trim();
  // Only the marker decides deprecation, matching the case-sensitive substring test SelectEnvTypeStep
  // uses to hide retired types. The underscore prefix alone does not: it is documented as a cosmetic
  // sort prefix, so a type carrying one is still selectable for new workspaces.
  const deprecated = isDeprecatedName(raw);
  // Stripping runs unconditionally. The underscore is never meaningful to a researcher, so it should
  // not leak into the OS row even on a type that is not deprecated.
  const cleaned = stripDeprecationMarkers(raw);

  if (cleaned === '') return { os: 'Unknown', software: 'Base', deprecated };

  const separatorIndex = cleaned.indexOf(' - ');
  if (separatorIndex > 0) {
    const os = cleaned.substring(0, separatorIndex).trim();
    const software = cleaned.substring(separatorIndex + 3).trim();
    return { os: os || 'Unknown', software: software || 'Base', deprecated };
  }

  return { os: cleaned, software: 'Base', deprecated };
}

export {
  parseConfigName,
  middleTruncate,
  formatConfigName,
  parseEnvTypeName,
  stripDeprecationMarkers,
  isDeprecatedName,
  DEFAULT_MAX_LENGTH,
  DEPRECATED_MARKER,
};
